import React, { useEffect, useState } from 'react';
import { TextField } from '@framework-controls/core';
import css from './InformationCard.module.scss';
import { strings } from 'src/localization';
import classNames from 'classnames';
interface IProps {
    id: string;
    label: string;
    value: string;
    editMode: boolean;
    masked: boolean;
    error: boolean;
    mfaEnabled: boolean | undefined;
    fieldCustValue: string;
    onEdit: (e: any, id: string) => void;
    onCancel: (id: string) => void;
    onChange: (id: string, value: string) => void;
    onBlur: (id: string) => void;
    disabled?: boolean;
    identityVerified: boolean;
    getFieldRef?: (el: HTMLElement) => void;
    theme: string;
}

const InformationCardField = (props: IProps) => {
    const [helperText, setHelperText] = useState('');
    const [showEdit, setShowEdit] = useState(false);

    // The phone regex should accept numbers formatted as (***) ***-#### or ##########
    const phoneRegex = new RegExp(/^[(]*(\d{3}|\*{3})[)]*[\s.-]?(\d{3}|\*{3})[\s.-]?(\d{4})$/);
    const emailRegex = new RegExp(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))/.source + /@/.source +
        /((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.source);

    const readOnlyField = ((props.masked && !props.identityVerified && !props.editMode) ||
        (!props.identityVerified && props.mfaEnabled && !!props.fieldCustValue)) &&
        !(props.id === 'emailAddress' && !props.fieldCustValue?.match(emailRegex)) &&
        !(props.id === 'phoneNumber' && !props.fieldCustValue?.match(phoneRegex));
    // const disabledField = !props.identityVerified && props.mfaEnabled && !!props.fieldCustValue;

    let inputClass = css.Input;
    if (readOnlyField) {
        inputClass = css.InputDisabled;
    }
    if (props.theme === 'cadillac' && readOnlyField) {
        inputClass = css.InputDisabledCadillac;
    }

    useEffect(() => {
        if (props.editMode) {
            document.getElementById(`${props.id}_input`)?.focus();
        }
    }, [props.id, props.editMode]);

    useEffect(() => {
        let text = '';

        if (props.error) {
            if (props.id === 'phoneNumber') {
                text = strings.ValidPhone;
            } else if (props.id === 'emailAddress') {
                if (props.value?.length > 62) {
                    text = strings.ValidEmailLength;
                } else {
                    text = strings.ValidEmail;
                }
            } else if (props.id === 'firstName') {
                if (props.value?.length === 0) {
                    text = strings.formatString(strings.EmptyNameError, props.label).toString();
                } else {
                    text = strings.ValidNameLength;
                }
            } else if (props.id === 'lastName') {
                if (props.value?.length === 0) {
                    text = strings.formatString(strings.EmptyNameError, props.label).toString();
                } else {
                    text = strings.ValidNameLength;
                }
            } else {
                text = strings.formatString(strings.EmptyNameError, props.label).toString();
            }
        } else if (!props.error && props.editMode) {
            text = strings.formatString(strings.CancelToolTip, props.label).toString();
        }

        setHelperText(text);
    }, [props.error, props.id, props.editMode, props.label, props.value?.length]);

    function focusHandler() {
        if ((props.masked || readOnlyField) && !props.editMode && !props.error) {
            setHelperText(strings.formatString(strings.EditToolTip, props.label).toString());
        }
        if (props.masked && props.value.includes('*') && !readOnlyField) {
            props.onChange(props.id, '');
        }
    }

    function blurHandler() {
        if ((props.masked || readOnlyField) && !props.editMode && !props.error) {
            setHelperText('');
        }

        props.onBlur(props.id);
    }

    const editLinkClassName = classNames(
        css.editLinkField,
        {
            [css.editLinkFieldCV]: (props.theme === 'chevrolet'),
            [css.editLinkFieldMB]: (props.theme === 'mercedesBenz'),
            [css.editLinkFieldSPM]: (props.theme === 'toyotaSPM'),
            [css.editLinkFieldSPMHelperText]: (props.theme === 'toyotaSPM' && !props.error && helperText !== ''),
            [css.editLinkFieldSPMCancel]: (props.theme === 'toyotaSPM' && props.editMode),
            [css.editLinkFieldSPMCancelEmail]: (props.theme === 'toyotaSPM' && props.editMode && props.id === 'emailAddress' &&
                props.error),
            [css.editLinkFieldHonda]: (props.theme === 'honda'),
            [css.editLinkFieldBuick]: (props.theme === 'buick'),
            [css.editLinkFieldBMW]: (props.theme === 'bmw'),
            [css.editLinkFieldFord]: (props.theme === 'ford'),
            [css.editLinkFieldMazda]: (props.theme === 'mazda'),
            [css.editLinkFieldGenesis]: (props.theme === 'genesis'),
            [css.editLinkFieldAcura]: (props.theme === 'acura'),
            [css.editLinkFieldLexus]: (props.theme === 'lexus'),
        },
    );

    let editLink = <></>;

    if ((props.masked && !props.disabled && readOnlyField) || showEdit) {
        if (!props.editMode) {
            editLink = (
                <div
                    id={`edit_${props.id}`}
                    className={editLinkClassName}
                    onClick={(e: any) => props.onEdit(e, props.id)}
                >
                    {strings.Edit}
                </div>
            );
            if (!props.mfaEnabled && !showEdit) {
                setShowEdit(true);
            }
        } else {
            editLink = (
                <div
                    id={`cancel_${props.id}`}
                    className={editLinkClassName}
                    onClick={() => props.onCancel(props.id)}
                >
                    {strings.Cancel}
                </div>
            );
        }
    }

    return (
        <div className={inputClass}>
            <TextField
                id={props.id}
                label={`${props.label} *`}
                value={props.value ? props.value : ''}
                size='full'
                type='text'
                error={props.error}
                onChange={props.onChange}
                readOnly={readOnlyField}
                helperText={helperText}
                onFocus={focusHandler}
                onBlur={blurHandler}
                disabled={props.disabled}
                getFieldRef={props.getFieldRef}
            />

            {!props.mfaEnabled ? editLink : null}
        </div>
    );
};

export default InformationCardField;
